/**
 * Centralized Model Registry
 *
 * Single source of truth for all model definitions across the application.
 * All model metadata, capabilities, and costs are defined here.
 *
 * When adding a new model or provider:
 * 1. Add the model(s) to MODEL_REGISTRY
 * 2. The convenience exports (ANTHROPIC_MODELS, OPENAI_MODELS) auto-update
 * 3. Update llm-connections.ts if adding a new built-in connection
 */

// ============================================
// TYPES
// ============================================

/**
 * Provider identifier for AI backends.
 */
export type ModelProvider = 'anthropic' | 'pi';

/**
 * Full model definition with capabilities and costs.
 * Used throughout the application for model selection and display.
 */
export interface ModelDefinition {
  /** Model identifier (e.g., 'claude-sonnet-4-6', 'gpt-5.3-codex') */
  id: string;
  /** Human-readable name (e.g., 'Sonnet 4.6', 'Codex') */
  name: string;
  /** Short display name for compact UI (e.g., 'Sonnet', 'Codex') */
  shortName: string;
  /** Brief description of the model's strengths */
  description: string;
  /** Provider that offers this model */
  provider: ModelProvider;
  /** Maximum context window in tokens */
  contextWindow: number;
  /** Whether this model supports thinking/reasoning effort. Defaults to true when undefined. */
  supportsThinking?: boolean;
  /** Actual model ID sent to the API. Defaults to `id` when omitted.
   *  Used when multiple registry entries map to the same API model (e.g., 1M vs 200K context variants). */
  apiModelId?: string;
  /** Beta feature flags to include in SDK options when required by the API. */
  betas?: string[];
}

// ============================================
// MODEL REGISTRY (Single Source of Truth)
// ============================================

/**
 * All available models across all providers.
 * This is the authoritative list - all other model arrays derive from this.
 */
export const MODEL_REGISTRY: ModelDefinition[] = [
  // ----------------------------------------
  // Anthropic Claude Models
  // ----------------------------------------
  // NOTE: The app's default flagship model is NOT determined by registry order.
  // It is controlled by the single DEFAULT_MODEL_SHORT_NAME constant below, which
  // drives DEFAULT_MODEL, getDefaultModelsForConnection() ordering, and the chat
  // input footer label. Change that one constant to swap the default model.
  //
  // Like Opus 4.8 (see note below), we send the `context-1m-2025-08-07` beta
  // header so the Claude Agent SDK's internal model-window resolver unlocks the
  // full 1M window and does not auto-compact early. The header is a no-op on the
  // API side when the model already serves 1M by default.
  {
    id: 'claude-opus-5-5',
    apiModelId: 'claude-opus-5-5',
    name: 'Opus 5.5 (1M)',
    shortName: 'Opus 5.5',
    description: 'Complex agentic coding and enterprise work',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  {
    id: 'claude-opus-5',
    apiModelId: 'claude-opus-5',
    name: 'Opus 5 (1M)',
    shortName: 'Opus 5',
    description: 'Previous generation flagship model',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  // Fable 5.1 (launched 2026-09-01) replaces Fable 5 as the curated "Fable"
  // managed preset. Like Opus 5 above, we send the `context-1m-2025-08-07`
  // beta header so the Claude Agent SDK's model-window resolver unlocks the
  // full 1M window instead of auto-compacting at ~187K. The header is a no-op
  // API-side when the model already serves 1M by default.
  //
  // The Fable 5.0 entries were removed in v0.7.427. Sessions still pinned to
  // `claude-fable-5` are carried onto 5.1 by IN_FAMILY_MODEL_UPGRADES below.
  {
    id: 'claude-fable-5-1',
    apiModelId: 'claude-fable-5-1',
    name: 'Fable 5.1 (1M)',
    shortName: 'Fable',
    description: 'Most capable, extended context',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  {
    id: 'claude-fable-5-1-200k',
    apiModelId: 'claude-fable-5-1',
    name: 'Fable 5.1 (200K)',
    shortName: 'Fable 200K',
    description: 'Standard context window',
    provider: 'anthropic',
    contextWindow: 200_000,
  },
  // Opus 4.8 serves the full 1M context window by default with NO beta header
  // on the Claude API, Amazon Bedrock, and Vertex AI (200K on Microsoft Foundry).
  // See: https://platform.claude.com/docs/en/about-claude/models/whats-new-claude-4-8
  //
  // We still send the `context-1m-2025-08-07` beta header even though the API
  // doesn't require it. The Claude Agent SDK's internal `Xk()` model-window
  // resolver (used to compute the auto-compact threshold) returns 200K for
  // `claude-opus-4-8` unless the model id ends in `[1m]` OR the 1M beta header
  // is present. Without this, the SDK auto-compacts around 187K tokens even
  // though our UI advertises a 1M window. The header is a no-op on the API
  // side for Opus 4.8 but unlocks the SDK's full-window codepath.
  {
    id: 'claude-opus-4-8',
    apiModelId: 'claude-opus-4-8',
    name: 'Opus 4.8 (1M)',
    shortName: 'Opus',
    description: 'Most capable, extended context',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  {
    id: 'claude-opus-4-8-200k',
    apiModelId: 'claude-opus-4-8',
    name: 'Opus 4.8 (200K)',
    shortName: 'Opus 200K',
    description: 'Standard context window',
    provider: 'anthropic',
    contextWindow: 200_000,
  },
  {
    id: 'claude-opus-4-7',
    apiModelId: 'claude-opus-4-7',
    name: 'Opus 4.7 (1M)',
    shortName: 'Opus 4.7',
    description: 'Previous generation flagship model',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  {
    id: 'claude-opus-4-7-200k',
    apiModelId: 'claude-opus-4-7',
    name: 'Opus 4.7 (200K)',
    shortName: 'Opus 4.7 200K',
    description: 'Previous generation, standard context',
    provider: 'anthropic',
    contextWindow: 200_000,
  },
  {
    id: 'claude-opus-4-6',
    name: 'Opus 4.6',
    shortName: 'Opus 4.6',
    description: 'Previous generation flagship model',
    provider: 'anthropic',
    contextWindow: 1_000_000,
  },
  {
    id: 'claude-opus-4-5-20251101',
    name: 'Opus 4.5',
    shortName: 'Opus 4.5',
    description: 'Previous generation flagship model',
    provider: 'anthropic',
    contextWindow: 200_000,
  },
  {
    id: 'claude-sonnet-4-6',
    name: 'Sonnet 4.6',
    shortName: 'Sonnet',
    description: 'Best for everyday tasks',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    betas: ['context-1m-2025-08-07'],
  },
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Haiku 4.5',
    shortName: 'Haiku',
    description: 'Fastest for quick answers',
    provider: 'anthropic',
    contextWindow: 200_000,
  },

  // ----------------------------------------
  // Pi Models
  // No hardcoded entries — models are discovered dynamically:
  //   - Pi: getModels(provider) from @mariozechner/pi-ai SDK
  // See ModelRefreshService in apps/electron/src/main/model-fetchers/
  // ----------------------------------------
];

// ============================================
// PROVIDER-FILTERED EXPORTS
// ============================================

/**
 * Get models filtered by provider.
 */
export function getModelsByProvider(provider: ModelProvider): ModelDefinition[] {
  return MODEL_REGISTRY.filter(m => m.provider === provider);
}

/** All Anthropic Claude models */
export const ANTHROPIC_MODELS = getModelsByProvider('anthropic');


/**
 * Legacy compatibility export.
 * Used by existing code that imports MODELS (expects Claude models only).
 * @deprecated Use ANTHROPIC_MODELS or MODEL_REGISTRY instead
 */
export const MODELS = ANTHROPIC_MODELS;

// ============================================
// MODEL ID HELPERS (Derived from Registry)
// ============================================

/** Get the first model ID matching a short name, or undefined if not found */
function findModelIdByShortName(shortName: string): string | undefined {
  return MODEL_REGISTRY.find(m => m.shortName === shortName)?.id;
}

/** Get the first model ID matching a short name (throws if not found) */
export function getModelIdByShortName(shortName: string): string {
  const id = findModelIdByShortName(shortName);
  if (!id) throw new Error(`Model not found: ${shortName}`);
  return id;
}

// ============================================
// CONNECTION DEFAULTS
// Used ONLY when writing defaults to LLM connection config (not as runtime fallbacks).
// ============================================

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ DEFAULT MODEL — single point of control                                   │
 * └─────────────────────────────────────────────────────────────────────────┘
 * Change this ONE value to swap the app's default flagship model. Everything
 * derives from it:
 *   - DEFAULT_MODEL                       (connection backfill / default model)
 *   - getDefaultModelsForConnection()     (puts this model first → the default)
 *   - getDefaultModelLabel()              ("Anthropic Claude …" chat-input footer)
 *
 * Claude Opus 5.5 replaces Opus 5 as Relay's default model at high
 * reasoning effort.
 *
 * Must match the `shortName` of an entry in MODEL_REGISTRY above.
 */
export const DEFAULT_MODEL_SHORT_NAME = 'Opus 5.5';

/** Default model for Anthropic connections (used when creating/backfilling connections) */
export const DEFAULT_MODEL = getModelIdByShortName(DEFAULT_MODEL_SHORT_NAME);

/**
 * Marketing-style label for the default model, shown under the chat input,
 * e.g. "Anthropic Claude Opus 4.8". Derives from DEFAULT_MODEL so the footer
 * updates automatically whenever the default model is swapped.
 */
export function getDefaultModelLabel(): string {
  const name = getModelDisplayName(DEFAULT_MODEL).replace(/\s*\((?:\d+[KM])\)\s*$/i, '');
  return `Anthropic Claude ${name}`;
}

/**
 * Anthropic flagship model IDs that the app used as its DEFAULT in a previous
 * version but no longer does. A session still pinned to one of these is
 * auto-upgraded to the current DEFAULT_MODEL on the next message send (see
 * upgradeSupersededDefaultModel), so users are never stuck on a stale flagship
 * after the app swaps its default model.
 *
 * ⚠️ When you change DEFAULT_MODEL_SHORT_NAME, add the OUTGOING default's model
 * IDs here (both the 1M base id AND its `-200k` variant). Only ever list former
 * *defaults* — never a model a user might deliberately pick and want to keep
 * (e.g. Sonnet/Haiku). The current DEFAULT_MODEL must NOT appear in this set.
 *
 * NOTE: the Fable 5 entries below are intercepted by IN_FAMILY_MODEL_UPGRADES
 * and land on Fable 5.1, not on DEFAULT_MODEL.
 */
export const SUPERSEDED_DEFAULT_ANTHROPIC_MODEL_IDS: ReadonlySet<string> = new Set([
  'claude-opus-4-5-20251101',
  'claude-opus-4-6', 'claude-opus-4-6-200k',
  'claude-opus-4-7', 'claude-opus-4-7-200k',
  'claude-opus-4-8', 'claude-opus-4-8-200k',
  'claude-fable-5', 'claude-fable-5-200k',
  'claude-opus-5',
]);

/**
 * Superseded model IDs that upgrade WITHIN their own model family instead of
 * onto DEFAULT_MODEL. Checked BEFORE the DEFAULT_MODEL fallback below.
 *
 * Fable was Relay's default for a single release (v0.7.415) and is now a
 * deliberate, non-default choice offered through the curated `fable-5` managed
 * preset. A Fable *point release* must therefore keep those users on Fable
 * rather than sweeping them onto Opus 5 — which is what the plain superseded
 * path would do, since `claude-fable-5` is still listed above.
 *
 * ⚠️ Only map onto model IDs that exist in MODEL_REGISTRY.
 */
const IN_FAMILY_MODEL_UPGRADES: ReadonlyMap<string, string> = new Map([
  ['claude-fable-5', 'claude-fable-5-1'],
  ['claude-fable-5-200k', 'claude-fable-5-1-200k'],
]);

/**
 * If `modelId` is a superseded former-default Anthropic flagship, return its
 * replacement. Two tiers:
 *
 *  1. **In-family upgrade** — models listed in IN_FAMILY_MODEL_UPGRADES move to
 *     the newer version of the SAME family (e.g. Fable 5 → Fable 5.1), because
 *     the user deliberately chose that family.
 *  2. **Default fallback** — every other superseded ID moves to the current
 *     DEFAULT_MODEL, preserving the 200K context variant when one exists.
 *
 * Provider-prefixed ids (e.g. `anthropic/claude-opus-4-8`) are handled, and any
 * model that is not a former default (Sonnet/Haiku, OpenAI/Pi/Gemini models, or
 * the current default itself) is returned untouched — membership in
 * SUPERSEDED_DEFAULT_ANTHROPIC_MODEL_IDS is the guard, so deliberate non-default
 * choices are preserved.
 */
export function upgradeSupersededDefaultModel(modelId: string): string {
  if (!modelId) return modelId;
  const bare = modelId.includes('/') ? modelId.split('/').pop()! : modelId;
  const inFamily = IN_FAMILY_MODEL_UPGRADES.get(bare);
  if (inFamily) return inFamily;
  if (!SUPERSEDED_DEFAULT_ANTHROPIC_MODEL_IDS.has(bare)) return modelId;
  if (bare.endsWith('-200k')) {
    const variant = `${DEFAULT_MODEL}-200k`;
    if (getModelById(variant)) return variant;
  }
  return DEFAULT_MODEL;
}


// ============================================
// UTILITY MODELS
// ============================================

/**
 * Get the default summarization model ID (Haiku).
 * Used as fallback when no connection context is available
 * (e.g., url-validator, mcp/validation, summarize.ts without modelOverride).
 *
 * For connection-aware summarization model resolution, use
 * getSummarizationModel(connection) from llm-connections.ts instead.
 */
export function getDefaultSummarizationModel(): string {
  return findModelIdByShortName('Haiku') ?? DEFAULT_MODEL;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get a model by ID from the registry.
 */
export function getModelById(modelId: string): ModelDefinition | undefined {
  return MODEL_REGISTRY.find(m => m.id === modelId);
}

/**
 * Get display name for a model ID (full name with version).
 */
export function getModelDisplayName(modelId: string): string {
  const model = getModelById(modelId);
  if (model) return model.name;
  // Fallback: strip prefix and date suffix, format nicely
  // e.g., "claude-opus-4-5-20251101" → "Opus 4.5"
  const stripped = modelId
    .replace('claude-', '')
    .replace(/-\d{8}$/, '');  // Remove date suffix
  // Split on dashes, capitalize first part, join version parts with dots
  const parts = stripped.split('-');
  const first = parts[0];
  if (!first) return modelId;
  const name = first.charAt(0).toUpperCase() + first.slice(1);
  const version = parts.slice(1).join('.');
  return version ? `${name} ${version}` : name;
}

/**
 * Get short display name for a model ID (without version number).
 */
export function getModelShortName(modelId: string): string {
  const model = getModelById(modelId);
  if (model) return model.shortName;
  // For provider-prefixed IDs (e.g. "openai/gpt-5"), show just the model part
  if (modelId.includes('/')) {
    return modelId.split('/').pop() || modelId;
  }
  // Fallback: strip claude- prefix and date suffix, then capitalize
  const stripped = modelId.replace('claude-', '').replace(/-[\d.-]+$/, '');
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

/**
 * Get known context window size for a model ID.
 */
export function getModelContextWindow(modelId: string): number | undefined {
  return getModelById(modelId)?.contextWindow;
}

/**
 * Get the API model ID for a registry model.
 * For models with an apiModelId override (e.g., context variants that share the same
 * underlying API model), returns the API-level model ID. Falls back to the model's id.
 */
export function getApiModelId(modelId: string): string {
  return getModelById(modelId)?.apiModelId ?? modelId;
}

/**
 * Get the beta features required for a model ID.
 * Returns undefined when no betas are needed (standard context models).
 */
export function getModelBetas(modelId: string): string[] | undefined {
  return getModelById(modelId)?.betas;
}

/**
 * Check if model is an Opus model (for cache TTL decisions).
 */
export function isOpusModel(modelId: string): boolean {
  return modelId.includes('opus');
}

/**
 * Check if a model is Opus 4.7 or later (adaptive-thinking-only generation).
 *
 * These models share two API constraints inherited from 4.7:
 *  - `temperature`, `top_p`, `top_k` return 400 if set to non-default values
 *  - Manual extended-thinking budgets are rejected (only adaptive thinking is allowed)
 *
 * Matches the canonical IDs (`claude-opus-4-7`, `claude-opus-4-8`,
 * `claude-opus-5`) and provider-prefixed variants.
 */
export function isOpus47OrLater(modelId: string): boolean {
  // Matches claude-opus-4-7, -4-8, -4-9, -4-10 … -4-99 (with optional -200k or
  // provider prefix like "anthropic/"). The trailing (?:-|$) ensures the
  // version number is complete and does NOT match date-suffixed legacy IDs
  // such as claude-opus-4-20250514 (the `20...` would otherwise look like v20).
  return typeof modelId === 'string'
    && /(?:^|\/)claude-opus-(?:5(?:-|$)|4-(?:[7-9]|[1-9]\d)(?:-|$))/i.test(modelId);
}

/**
 * Check if a model is "adaptive-thinking-only" — i.e. it shares the API
 * constraints introduced with the Opus 4.7 generation:
 *  - `temperature`, `top_p`, `top_k` return 400 if set to non-default values
 *  - Manual extended-thinking budgets are rejected (only adaptive thinking is allowed)
 *
 * This is the family-agnostic predicate. It currently matches Opus 4.7+ and
 * Claude Fable 5. Prefer this over `isOpus47OrLater()` for capability checks
 * that should apply to all modern adaptive-thinking models, not just Opus.
 */
export function isAdaptiveThinkingOnly(modelId: string): boolean {
  return isOpus47OrLater(modelId) || /(?:^|\/)claude-fable-5(?:-|$)/i.test(modelId);
}

/**
 * Check if a model rejects custom sampling parameters (temperature, top_p, top_k).
 * The Anthropic API returns 400 if these are set on adaptive-thinking models
 * (Opus 4.7+, Fable 5).
 */
export function rejectsCustomSamplingParams(modelId: string): boolean {
  return isAdaptiveThinkingOnly(modelId);
}

/**
 * Check if a model has "always-on" adaptive thinking that CANNOT be disabled.
 *
 * On the Claude Fable 5 / Mythos 5 generation, adaptive thinking is the only
 * thinking mode: `thinking: { type: "disabled" }` returns a 400 error (and so
 * do manual extended-thinking budgets / assistant prefill). To honor an "off"
 * thinking preference on these models you must instead request adaptive
 * thinking at the lowest effort, not disable thinking.
 *
 * NOTE: this is narrower than `isAdaptiveThinkingOnly()`. Opus 4.7/4.8 are also
 * adaptive-thinking-only for *budgets*, but they still accept
 * `thinking: { type: "disabled" }`, so they are intentionally excluded here.
 *
 * See: https://platform.claude.com/docs/en/about-claude/models/introducing-claude-fable-5-and-claude-mythos-5
 */
export function isThinkingAlwaysOn(modelId: string): boolean {
  return /(?:^|\/)claude-(?:fable|mythos)-5(?:-|$)/i.test(modelId);
}

/**
 * Check if a model ID refers to a Claude model.
 * Handles both direct Anthropic IDs (e.g. "claude-sonnet-4-6")
 * and provider-prefixed IDs (e.g. "anthropic/claude-sonnet-4" via OpenRouter).
 */
export function isClaudeModel(modelId: string): boolean {
  const lower = modelId.toLowerCase();
  return lower.startsWith('claude-') || lower.includes('/claude');
}


/**
 * Get the provider for a model ID.
 */
export function getModelProvider(modelId: string): ModelProvider | undefined {
  return getModelById(modelId)?.provider;
}
